import { computed } from 'vue'
import { useRoute } from '#app/composables/router'
import {
  useAppliedFilters,
  useProductsForListing,
} from '#storefront-product-listing'
import { PRODUCT_TILE_WITH_PARAMS, PRODUCTS_PER_PAGE } from '~/constants'

function isLocalePattern(part: string): boolean {
  return /^[a-z]{2}-[a-z]{2}$/i.test(part)
}

function hasProductNamePattern(part: string): boolean {
  // Looks like a product/category/brand slug ending with -<number>
  return Boolean(part && /-\d+$/.test(part))
}

function removeTrailingId(part: string): string {
  // Remove only the trailing "-<number>" if present
  return part.replace(/-\d+$/, '')
}

function getStartIndex(parts: string[]): number {
  // Example: /xx-xx/p/men/adidas-originals-superstar-ii-weiss-45376
  // If parts[2] is not a product name pattern (like "men"), skip it
  if (parts.length > 2 && !hasProductNamePattern(parts[2])) {
    return 3
  }
  return 2
}

function extractNameFromParts(remainingParts: string[]): string | null {
  if (remainingParts.length === 0) {
    return null
  }

  const lastPart = remainingParts[remainingParts.length - 1]
  const isLastPartNumeric = /^\d+$/.test(lastPart)

  // If the last part is numeric (e.g., product ID),
  // take the previous part and remove its ID
  if (isLastPartNumeric && remainingParts.length > 1) {
    const productPart = remainingParts[remainingParts.length - 2]
    return removeTrailingId(productPart)
  }

  // Otherwise, remove the ID from the last part if it exists
  return removeTrailingId(lastPart)
}

function handleProductPath(parts: string[]): string | null {
  const startIndex = getStartIndex(parts)
  const remainingParts = parts.slice(startIndex)
  return extractNameFromParts(remainingParts)
}

export function getPathPart(path: string | undefined): string | boolean {
  if (!path) {
    return ''
  }

  const route = useRoute()
  const parts = path.split('/').filter(Boolean)

  if (parts.length < 2 || !isLocalePattern(parts[0])) {
    return ''
  }

  const type = parts[1]

  if (['p', 'b', 'c'].includes(type)) {
    return handleProductPath(parts) ?? ''
  }

  if (type.includes('search')) {
    const { appliedFilter } = useAppliedFilters(route)
    const { totalProductsCount } = useProductsForListing({
      params: computed(() => ({
        where: appliedFilter.value,
        perPage: PRODUCTS_PER_PAGE,
        page: Number(route.query.page) || 1,
        with: PRODUCT_TILE_WITH_PARAMS,
        includeSoldOut: false,
        includeSellableForFree: false,
      })),
    })

    return totalProductsCount.value > 0
  }
  if (route.meta.SnPageType === 'homepage') {
    return type
  }
  return ''
}
